/**
 * Roster Validator - Code-based validation for extracted roster data
 *
 * Validates extracted player data for errors and warnings.
 * Ported from mobile/utils/roster-validator.ts for full feature parity.
 */

import type { ParsedCSV, ValidationResult } from '../types/extraction';
import { getValidGradYearRange } from '../constants/validation';

/**
 * Validate roster data after extraction
 * @param parsedData - Parsed CSV data
 * @returns ValidationResult with errors and warnings
 */
export function validateRoster(parsedData: ParsedCSV): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // 1. Check for missing names
  validateNames(parsedData, errors);

  // 2. Check team assignments (showcase only)
  if (parsedData.rosterType === 'showcase') {
    validateTeamAssignments(parsedData, warnings);
  }

  // 3. Check for duplicate numbers within teams
  validateDuplicateNumbers(parsedData, warnings);

  // 4. Check for suspicious values
  validateDataReasonableness(parsedData, warnings);

  const isValid = errors.length === 0;

  return {
    isValid,
    errors,
    warnings,
  };
}

/**
 * Validate that players have names
 */
function validateNames(parsedData: ParsedCSV, errors: string[]): void {
  for (let i = 0; i < parsedData.rows.length; i++) {
    const row = parsedData.rows[i];

    // Must have at least first name OR last name
    const hasFirstName = row.first_name && row.first_name !== '' && row.first_name !== '-';
    const hasLastName = row.last_name && row.last_name !== '' && row.last_name !== '-';

    if (!hasFirstName && !hasLastName) {
      errors.push(
        `Row ${i + 1}: Player has no name (number: ${row.number || 'N/A'})`
      );
    }
  }
}

/**
 * Validate team assignments for showcase rosters
 */
function validateTeamAssignments(parsedData: ParsedCSV, warnings: string[]): void {
  for (let i = 0; i < parsedData.rows.length; i++) {
    const row = parsedData.rows[i];

    const hasTeam = row.team_id && row.team_id !== '' && row.team_id !== '-';

    if (!hasTeam) {
      const name = `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'Unknown';
      warnings.push(
        `Row ${i + 1}: Player ${name} has no team assignment`
      );
    }
  }
}

/**
 * Check for duplicate jersey numbers within teams
 */
function validateDuplicateNumbers(
  parsedData: ParsedCSV,
  warnings: string[]
): void {
  const rosterType = parsedData.rosterType || 'single_team';

  // Group players by team
  const playersByTeam = new Map<string, typeof parsedData.rows>();

  for (const row of parsedData.rows) {
    const teamKey = rosterType === 'showcase'
      ? `${row.team_id || 'unknown'}-${row.team_color || 'unknown'}`
      : 'single';

    if (!playersByTeam.has(teamKey)) {
      playersByTeam.set(teamKey, []);
    }
    playersByTeam.get(teamKey)!.push(row);
  }

  // Check for duplicates within each team
  for (const [teamKey, teamPlayers] of playersByTeam) {
    const numberCounts = new Map<string, number>();

    for (const player of teamPlayers) {
      const number = player.number?.toString();
      if (number && number !== '' && number !== '-') {
        const count = (numberCounts.get(number) || 0) + 1;
        numberCounts.set(number, count);
      }
    }

    // Report duplicates
    for (const [number, count] of numberCounts) {
      if (count > 1) {
        warnings.push(
          `Team ${teamKey}: Jersey number ${number} appears ${count} times`
        );
      }
    }
  }
}

/**
 * Validate data reasonableness
 */
function validateDataReasonableness(
  parsedData: ParsedCSV,
  warnings: string[]
): void {
  const { min: minYear, max: maxYear } = getValidGradYearRange();

  for (let i = 0; i < parsedData.rows.length; i++) {
    const row = parsedData.rows[i];

    // Check grad year is reasonable
    if (row.grad_year || row.class) {
      const yearValue = row.grad_year || row.class;
      const year = typeof yearValue === 'number' ? yearValue : parseInt(yearValue?.toString() || '', 10);

      if (!isNaN(year)) {
        if (year < minYear || year > maxYear) {
          const name = `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'Unknown';
          warnings.push(
            `Row ${i + 1}: Graduation year ${year} seems unusual for ${name}`
          );
        }
      }
    }

    // Check email format (basic check)
    if (row.email && row.email !== '' && row.email !== '-') {
      const emailStr = row.email.toString();
      if (!emailStr.includes('@') || !emailStr.includes('.')) {
        warnings.push(
          `Row ${i + 1}: Email "${emailStr}" may be invalid`
        );
      }
    }
  }
}
